import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormControl } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { environment } from 'environments/environment';
import { BehaviorSubject, catchError, delay, map, Observable, of, tap, throwError } from 'rxjs';

import { CashboxCloseComponent } from '../components/cashbox-close/cashbox-close.component';
import { CashboxOpenComponent } from '../components/cashbox-open/cashbox-open.component';
import { Cashbox } from '../models/cashbox.model';
import { AnalyticsService } from './analytics.service';
import { CommerceService } from './commerce.service';

@Injectable({ providedIn: 'root' })
export class CashboxService {
    API_URL = environment.API_URL;
    private _cashbox$ = new BehaviorSubject<Cashbox>(this.createEmptyCashbox());
    public loading$ = new BehaviorSubject<boolean>(false);
    private hasAskedToCloseToday = false;

    private _isCashboxOpen$ = new BehaviorSubject<boolean>(false);

    public currentPage = 1;
    public pageSize = 10;
    public totalDocs = 0;
    public totalPages = 1;
    public searchControl = new FormControl('');
    public sortedColumn = 'openingDate';
    public sortedDirection = 'desc';
    public useScore = true;

    public countUserBills = 0;
    public openedFromScFinish = false;

    private _cashboxes$ = new BehaviorSubject<Cashbox[]>([]);
    public cashboxes$ = this._cashboxes$.asObservable();

    constructor(private http: HttpClient, private modalService: NgbModal, private commerceService: CommerceService, private analyticsService: AnalyticsService) {}

    get cashbox$(): Observable<Cashbox> {
        return this._cashbox$.asObservable();
    }

    get currentCashbox(): Cashbox {
        return this._cashbox$.value;
    }

    set isCashboxOpen(open: boolean) {
        this._isCashboxOpen$.next(open);
    }

    get isCashboxOpen$(): BehaviorSubject<boolean> {
        return this._isCashboxOpen$;
    }

    getBillCountUserPerDay$(): Observable<any> {
        return this.http.get<any>(`${this.API_URL}/cashboxes/count-user-day`);
    }

    getCashboxes$(): Observable<{ cashboxes: Cashbox[]; totalDocs: number; totalPages: number }> {
        const searchValue = this.searchControl.value || '';

        const params = new HttpParams()
            .set('currentPage', this.currentPage.toString())
            .set('pageSize', this.pageSize.toString())
            .set('search', searchValue)
            .set('sortedColumn', this.sortedColumn || 'openingDate')
            .set('sortedDirection', this.sortedDirection || 'desc')
            .set('useScore', this.useScore.toString());

        // Set headers to ensure proper content type
        const headers = {
            'Content-Type': 'application/json',
            Accept: 'application/json',
        };

        return this.http
            .get<{
                title: string;
                message: string;
                data: Cashbox[];
                totalDocs: number;
                totalPages: number;
                currentPage: string;
            }>(`${this.API_URL}/cashboxes`, { params, headers })
            .pipe(
                map(raw => {
                    return {
                        cashboxes: raw.data || [],
                        totalDocs: raw.totalDocs || 0,
                        totalPages: raw.totalPages || 1,
                        currentPage: +raw.currentPage || 1,
                    };
                }),
                catchError(error => {
                    return throwError(() => new Error('Failed to load cashboxes: ' + (error.error?.message || error.message)));
                }),
            );
    }

    public loadCashboxes() {
        this.loading$.next(true);
        this.getCashboxes$().subscribe({
            next: res => {
                this.updateCashboxes(res.cashboxes);
                this.totalDocs = res.totalDocs;
                this.totalPages = res.totalPages;
                this.loading$.next(false);
            },
            error: err => {
                this.loading$.next(false);
            },
        });
    }

    nextPage() {
        if (this.currentPage < this.totalPages) {
            this.currentPage++;
            this.loadCashboxes();
        }
    }

    previousPage() {
        if (this.currentPage > 1) {
            this.currentPage--;
            this.loadCashboxes();
        }
    }

    goToPage(page: number) {
        if (page >= 1 && page <= this.totalPages) {
            this.currentPage = page;
            this.loadCashboxes();
        }
    }

    search() {
        const searchValue = this.searchControl.value;
        if (searchValue === null || searchValue === undefined) {
            this.searchControl.setValue('');
        }

        this.currentPage = 1;
        this.loading$.next(true);

        this.getCashboxes$().subscribe({
            next: res => {
                this.updateCashboxes(res.cashboxes);
                this.totalDocs = res.totalDocs;
                this.totalPages = res.totalPages;
                this.loading$.next(false);
            },
            error: err => {
                console.error('Error searching cashboxes:', err);
                this.loading$.next(false);
            },
        });
    }

    onSort(sortEvent: { column: string; direction: string }) {
        this.sortedColumn = sortEvent.column;
        this.sortedDirection = sortEvent.direction;
        this.getCashboxes$().subscribe();
    }

    public updateCashboxes(cashboxes: Cashbox[]): void {
        this._cashboxes$.next(cashboxes);
    }

    createCashBox(initialAmountUSD: number): Observable<Cashbox> {
        const newCashbox = {
            initialAmountUSD,
            status: 'open',
            openingDate: new Date(),
        };

        return this.http.post<Cashbox>(`${this.API_URL}/cashboxes/open`, newCashbox).pipe(
            tap(created => {
                this.updateCashboxState({
                    ...created,
                    status: 'open',
                    openingDate: new Date(created.openingDate),
                });
            }),
            catchError(error => {
                this.resetCashboxState();
                return throwError(() => error);
            }),
        );
    }

    closeCashBox(finalAmountUSD: number): Observable<Cashbox> {
        if (!this.currentCashbox._id) {
            console.error('Cannot close cashbox: No active cashbox found.');
            return throwError(() => new Error('No active cashbox to close'));
        }

        const update = {
            finalAmountUSD,
            status: 'closed',
            closingDate: new Date(),
        };

        return this.http.patch<{ title: string; data: Cashbox }>(`${this.API_URL}/cashboxes/${this.currentCashbox._id}/close`, update).pipe(
            map(response => ({
                ...response.data,
            })),
            tap(closedCashbox => {
                this.updateCashboxState(closedCashbox);
                this.isCashboxOpen = false;
            }),
            catchError(error => {
                console.error('Failed to close cashbox:', error);
                this.revertCashboxStatus();
                return throwError(() => error);
            }),
        );
    }

    addObservationsToCashbox(cashboxId: string, observations: string): Observable<Cashbox> {
        return this.http.patch<{ title: string; data: Cashbox }>(`${this.API_URL}/cashboxes/${cashboxId}/observations`, { observations }).pipe(
            map(response => response.data),
            catchError(error => {
                console.error('Failed to add observations:', error);
                return throwError(() => error);
            }),
        );
    }

    private updateCashboxState(update: Partial<Cashbox> & { data?: Cashbox }): void {
        const current = this.currentCashbox;
        const updatedCashbox = update.data ? { ...update.data, ...update } : update;
        delete updatedCashbox.data;

        this._cashbox$.next({
            ...current,
            ...updatedCashbox,
            bankAccounts: updatedCashbox.bankAccounts || current.bankAccounts,
            sales: updatedCashbox.sales || current.sales,
        });
    }

    private resetCashboxState(): void {
        this._cashbox$.next(this.createEmptyCashbox());
    }

    private revertCashboxStatus(): void {
        const current = this.currentCashbox;
        this._cashbox$.next({
            ...current,
            status: current.status === 'open' ? 'closed' : 'open',
        });
    }

    loadOpenCashbox(): Observable<Cashbox> {
        this.checkAndPromptForCashboxClose();
        return this.http.get<{ title: string; data: Cashbox }>(`${this.API_URL}/cashboxes/active`).pipe(
            map(response => response.data ?? this.createEmptyCashbox()),
            tap(cashbox => {
                this._cashbox$.next(cashbox);
                this.isCashboxOpen = !!(cashbox && cashbox._id && cashbox.status === 'open');
            }),
            catchError(error => {
                if (error.status === 404) {
                    const emptyCashbox: Cashbox = this.createEmptyCashbox();
                    this._cashbox$.next(emptyCashbox);
                    this.isCashboxOpen = false;
                    return of(emptyCashbox);
                }
                console.error('Error fetching open cashbox:', error);
                return throwError(() => error);
            }),
        );
    }

    private createEmptyCashbox(): Cashbox {
        return {
            _id: '',
            initialAmountUSD: 0,
            status: 'closed',
            openedBy: {
                _id: '',
                name: '',
            },
            sales: [],
            bankAccounts: [],
            totalSales: 0,
            tenantId: '',
            openingDate: new Date(),
        };
    }

    public openCashboxModal() {
        // Check if cashbox experiment is enabled
        if (!this.analyticsService.getExperimentVariant('cashbox') || !this.commerceService.commerce.cashBox.enabled) {
            return;
        }

        const modalRef = this.modalService.open(CashboxOpenComponent, {
            size: 'lg',
            centered: true,
            backdrop: 'static',
            keyboard: false,
            animation: true,
        });

        modalRef.result.catch(res => {
            if (res === 'cancel') {
                this.isCashboxOpen = false;
                this.openedFromScFinish = false;
            }
        });
    }

    public closeCashboxModal(isForcedClose: boolean = false) {
        if (!this.analyticsService.getExperimentVariant('cashbox') || !this.commerceService.commerce.cashBox.enabled) {
            return;
        }
        const modalRef = this.modalService.open(CashboxCloseComponent, {
            windowClass: 'custom-modal',
            size: 'lg',
            centered: true,
            backdrop: 'static',
            keyboard: false,
        });

        modalRef.componentInstance.isForcedClose = isForcedClose;

        modalRef.result.catch(res => {
            if (res === 'cancel') {
                this.isCashboxOpen = true;
            }
        });
    }

    public checkAndPromptForCashboxClose(): boolean {
        if (this.hasAskedToCloseToday || !this.currentCashbox._id || this.currentCashbox.status !== 'open') {
            return false;
        }

        const openingDate = new Date(this.currentCashbox.openingDate);
        const today = new Date();
        const daysDifference = Math.floor((today.getTime() - openingDate.getTime()) / (1000 * 60 * 60 * 24));

        if (daysDifference > 0) {
            this.hasAskedToCloseToday = true;
            this.closeCashboxModal(true);
            return true;
        }

        return false;
    }

    public resetClosePrompt() {
        this.hasAskedToCloseToday = false;
    }
}
